import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ll_user") || "null"); } catch { return null; }
  });
  const [tenant, setTenant] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ll_tenant") || "null"); } catch { return null; }
  });
  const [impersonating, setImpersonating] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ll_impersonating") || "null"); } catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  const persist = useCallback((token, u, t) => {
    if (token) localStorage.setItem("ll_token", token);
    if (u) localStorage.setItem("ll_user", JSON.stringify(u));
    if (t) localStorage.setItem("ll_tenant", JSON.stringify(t));
    setUser(u);
    setTenant(t);
  }, []);

  /** Update session on workspace switch / token refresh. Triggers a `tenantChanged`
   * window event so subscribed pages can reload tenant-scoped data without a full
   * router remount. */
  const setSession = useCallback((token, u, t) => {
    persist(token, u, t);
    window.dispatchEvent(new CustomEvent("tenantChanged", { detail: { tenant: t } }));
  }, [persist]);

  /** Open a platform-admin impersonation session. Stashes the current
   * session under `ll_admin_*` so we can pop back to it later via
   * `exitImpersonation()` without forcing a re-login. */
  const startImpersonation = useCallback((token, u, t) => {
    const prevToken = localStorage.getItem("ll_token");
    const prevUser = localStorage.getItem("ll_user");
    const prevTenant = localStorage.getItem("ll_tenant");
    if (prevToken) localStorage.setItem("ll_admin_token", prevToken);
    if (prevUser) localStorage.setItem("ll_admin_user", prevUser);
    if (prevTenant) localStorage.setItem("ll_admin_tenant", prevTenant);
    const meta = {
      target_email: u?.email,
      target_tenant: t?.name,
      started_at: Date.now(),
      ttl_minutes: 15,
    };
    localStorage.setItem("ll_impersonating", JSON.stringify(meta));
    setImpersonating(meta);
    persist(token, u, t);
    window.dispatchEvent(new CustomEvent("tenantChanged", { detail: { tenant: t } }));
  }, [persist]);

  const exitImpersonation = useCallback(() => {
    const prevToken = localStorage.getItem("ll_admin_token");
    const prevUser = localStorage.getItem("ll_admin_user");
    const prevTenant = localStorage.getItem("ll_admin_tenant");
    localStorage.removeItem("ll_admin_token");
    localStorage.removeItem("ll_admin_user");
    localStorage.removeItem("ll_admin_tenant");
    localStorage.removeItem("ll_impersonating");
    setImpersonating(null);
    if (prevToken && prevUser) {
      persist(prevToken, JSON.parse(prevUser), prevTenant ? JSON.parse(prevTenant) : null);
      window.dispatchEvent(new CustomEvent("tenantChanged", { detail: { tenant: prevTenant ? JSON.parse(prevTenant) : null } }));
    }
  }, [persist]);

  const login = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    persist(res.data.token, res.data.user, res.data.tenant);
    return res.data;
  }, [persist]);

  const signup = useCallback(async (name, email, password, company_name, account_type = "individual") => {
    const res = await api.post("/auth/signup", { name, email, password, company_name, account_type });
    persist(res.data.token, res.data.user, res.data.tenant);
    return res.data;
  }, [persist]);

  const logout = useCallback(() => {
    localStorage.removeItem("ll_token");
    localStorage.removeItem("ll_user");
    localStorage.removeItem("ll_tenant");
    localStorage.removeItem("ll_admin_token");
    localStorage.removeItem("ll_admin_user");
    localStorage.removeItem("ll_admin_tenant");
    localStorage.removeItem("ll_impersonating");
    setUser(null);
    setTenant(null);
    setImpersonating(null);
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      localStorage.setItem("ll_user", JSON.stringify(res.data.user));
      localStorage.setItem("ll_tenant", JSON.stringify(res.data.tenant));
      setUser(res.data.user);
      setTenant(res.data.tenant);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (localStorage.getItem("ll_token") && !user) {
      setLoading(true);
      refreshMe().finally(() => setLoading(false));
    }
  }, [refreshMe, user]);

  return (
    <AuthContext.Provider value={{ user, tenant, loading, login, signup, logout, refreshMe, setSession, startImpersonation, exitImpersonation, impersonating }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
